import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isApiError } from '@shared/api/errors';
import type { AiReplyResponse } from '../api/ai-reply';
import { useHelpdeskRuntime } from './runtime';

type Result = AiReplyResponse['data'];
type ReviewReason =
  | 'reviewMessage'
  | 'reviewAttachments'
  | 'reviewContext'
  | 'reviewConversation'
  | 'reviewRecovery';
type State = {
  pending: boolean;
  error:
    | 'failed'
    | 'denied'
    | 'unavailable'
    | 'configurationUnavailable'
    | 'knowledgeUnavailable'
    | 'contextTooLarge'
    | 'unauthorized'
    | 'notFound'
    | 'rateLimited'
    | 'invalidOutput'
    | 'providerRejected'
    | 'timedOut'
    | 'uncertain'
    | null;
  suggestion: Result | null;
  reviewReason: ReviewReason | null;
  recovery: boolean;
};
const empty: State = {
  pending: false,
  error: null,
  suggestion: null,
  reviewReason: null,
  recovery: false,
};
const statusErrors: Partial<Record<number, State['error']>> = {
  401: 'unauthorized',
  403: 'denied',
  404: 'notFound',
  413: 'contextTooLarge',
  429: 'rateLimited',
};
const problemErrors: Partial<Record<string, State['error']>> = {
  APPLICATION_INTEGRATION_CONFIGURATION_UNAVAILABLE: 'configurationUnavailable',
  APPLICATION_INTEGRATION_KNOWLEDGE_UNAVAILABLE: 'knowledgeUnavailable',
  APPLICATION_INTEGRATION_INVALID_OUTPUT: 'invalidOutput',
  APPLICATION_INTEGRATION_PROVIDER_REJECTED: 'providerRejected',
};

/** Original invocation guards are never reconstructed for a replay. Only the opaque key survives reload. */
export function useAiReplyDraft(options: {
  scope: string;
  taskUuid: string;
  observation: string;
  draft: { revision: number; files: readonly File[] };
  enabled: boolean;
  apply: (result: Result) => void;
}) {
  const client = useQueryClient();
  const runtime = useHelpdeskRuntime();
  const current = useRef(options);
  current.current = options;
  const mounted = useRef(true);
  const revision = useRef(0);
  const previous = useRef(options);
  const lastChange = useRef<ReviewReason>('reviewContext');
  if (previous.current.observation !== options.observation) {
    lastChange.current =
      previous.current.scope !== options.scope ||
      previous.current.taskUuid !== options.taskUuid ||
      previous.current.draft.revision === options.draft.revision
        ? 'reviewContext'
        : previous.current.draft.files.length !== options.draft.files.length ||
            previous.current.draft.files.some((file, index) => file !== options.draft.files[index])
          ? 'reviewAttachments'
          : 'reviewMessage';
    revision.current += 1;
  }
  previous.current = options;
  const busy = useRef(false);
  const [states, setStates] = useState<Partial<Record<string, State>>>({});
  const identity = `${options.scope}:${options.taskUuid}`;
  const update = (id: string, value: Partial<State>) => {
    if (mounted.current)
      setStates((old) => ({ ...old, [id]: { ...(old[id] ?? empty), ...value } }));
  };
  useEffect(() => {
    mounted.current = true;
    const cache = client.getQueryCache();
    const threadData = new Map<string, unknown>(
      cache.getAll().map((query) => [query.queryHash, query.state.data])
    );
    const unsubscribe = cache.subscribe((event) => {
      const key = event.query.queryKey as readonly unknown[];
      if (key[0] !== 'helpdesk' || key[2] !== 'thread') return;
      const before = threadData.get(event.query.queryHash);
      const after: unknown = event.type === 'removed' ? undefined : event.query.state.data;
      if (event.type === 'removed') threadData.delete(event.query.queryHash);
      else threadData.set(event.query.queryHash, after);
      // Query structural sharing keeps identical responses stable. Fetch status and timestamps
      // are not conversation edits; changed or newly loaded pages still require review.
      if (
        before !== after &&
        key[1] === current.current.scope &&
        key[3] === current.current.taskUuid
      ) {
        revision.current += 1;
        lastChange.current = 'reviewConversation';
      }
    });
    return () => {
      mounted.current = false;
      revision.current += 1;
      lastChange.current = 'reviewContext';
      unsubscribe();
    };
  }, [client]);

  const generate = async () => {
    if (busy.current || !options.enabled) return;
    if (!runtime.generateReplyDraft) {
      update(identity, { error: 'unavailable' });
      return;
    }
    const original = { ...options, revision: revision.current, identity };
    const storageKey = `helpdesk-ai-reply:${identity}`;
    let key: string;
    let recovery: boolean;
    try {
      const retained = sessionStorage.getItem(storageKey);
      key = retained ?? crypto.randomUUID();
      recovery = retained !== null;
      sessionStorage.setItem(storageKey, key);
    } catch {
      update(identity, { error: 'unavailable' });
      return;
    }
    busy.current = true;
    update(identity, { pending: true, error: null, recovery });
    try {
      const response = await runtime.generateReplyDraft(original.taskUuid, key);
      const result = response.data;
      if (result.output.basis.taskUuid !== original.taskUuid) throw new Error('Wrong reply source');
      sessionStorage.removeItem(storageKey);
      const unchanged =
        mounted.current &&
        !recovery &&
        revision.current === original.revision &&
        current.current.enabled &&
        current.current.scope === original.scope &&
        current.current.taskUuid === original.taskUuid &&
        current.current.observation === original.observation;
      if (unchanged && result.output.currentAtCompletion) {
        current.current.apply(result);
        update(identity, { suggestion: null, reviewReason: null, recovery: false });
      } else
        update(identity, {
          suggestion: result,
          reviewReason: recovery
            ? 'reviewRecovery'
            : !current.current.enabled
              ? 'reviewContext'
              : lastChange.current,
          recovery: false,
        });
    } catch (error) {
      const status = isApiError(error) ? error.status : 0;
      const code = isApiError(error) ? error.code : '';
      // These typed failures are settled before provider dispatch. Failures after the dispatch
      // fence are reported as OUTCOME_UNCERTAIN and must retain their original key.
      const unavailableBeforeDispatch =
        status === 503 &&
        (code === 'APPLICATION_INTEGRATION_CONFIGURATION_UNAVAILABLE' ||
          code === 'APPLICATION_INTEGRATION_KNOWLEDGE_UNAVAILABLE');
      const rejectedOutput =
        status === 502 &&
        (code === 'APPLICATION_INTEGRATION_INVALID_OUTPUT' ||
          code === 'APPLICATION_INTEGRATION_PROVIDER_REJECTED');
      const uncertain =
        !unavailableBeforeDispatch &&
        !rejectedOutput &&
        (!status || status === 409 || status >= 500 || /uncertain/i.test(code));
      // Unknown gateway failures can hide a successful provider dispatch. Only a known terminal
      // failure permits a new explicit Generate action; never automatically resend.
      if (
        unavailableBeforeDispatch ||
        rejectedOutput ||
        (!uncertain && [400, 401, 403, 404, 413, 429].includes(status))
      ) {
        sessionStorage.removeItem(storageKey);
      }
      update(identity, {
        error:
          code === 'REQUEST_TIMEOUT'
            ? 'timedOut'
            : uncertain
              ? 'uncertain'
              : (statusErrors[status] ?? problemErrors[code] ?? 'failed'),
        recovery: uncertain,
      });
    } finally {
      busy.current = false;
      update(identity, { pending: false });
    }
  };
  const state = states[identity] ?? empty;
  return {
    ...state,
    generate,
    applySuggestion: () => {
      if (!options.enabled || !state.suggestion?.output.currentAtCompletion) return;
      options.apply(state.suggestion);
      update(identity, { suggestion: null, reviewReason: null });
    },
    dismissSuggestion: () => {
      update(identity, { suggestion: null, reviewReason: null });
    },
  };
}
